import { Card, Skeleton, Typography, theme as antdTheme } from 'antd';
import type { ReactNode } from 'react';

type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info';

export interface SummaryItem {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: Tone;
}

interface Props {
  items: SummaryItem[];
  isLoading?: boolean;
  /** Optional extra content rendered below the metric list inside the same card. */
  footer?: ReactNode;
  /**
   * Optional element rendered top-right inside the card. Use for a card-level
   * control such as a <RevealToggle/> when the rail contains masked figures.
   */
  headerExtra?: ReactNode;
}

export function SummaryRail({ items, isLoading, footer, headerExtra }: Props) {
  return (
    <Card styles={{ body: { padding: 16 } }}>
      {headerExtra && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          {headerExtra}
        </div>
      )}
      {isLoading ? (
        <Skeleton active paragraph={{ rows: 3 }} />
      ) : (
        <div style={{ display: 'grid', gap: 14 }}>
          {items.map((it, i) => (
            <SummaryRow key={i} item={it} />
          ))}
        </div>
      )}
      {footer && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--ant-color-border-secondary)' }}>
          {footer}
        </div>
      )}
    </Card>
  );
}

function SummaryRow({ item }: { item: SummaryItem }) {
  const { token } = antdTheme.useToken();
  const tone = item.tone ?? 'neutral';
  const valueColor =
    tone === 'success'
      ? token.colorSuccessText
      : tone === 'warning'
        ? token.colorWarningText
        : tone === 'danger'
          ? token.colorErrorText
          : tone === 'info'
            ? token.colorInfoText
            : token.colorText;

  return (
    <div style={{ display: 'grid', gap: 2 }}>
      <Typography.Text
        type="secondary"
        style={{ fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase' }}
      >
        {item.label}
      </Typography.Text>
      <Typography.Text style={{ fontSize: 22, fontWeight: 600, color: valueColor, lineHeight: 1.2 }}>
        {item.value}
      </Typography.Text>
      {item.hint && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {item.hint}
        </Typography.Text>
      )}
    </div>
  );
}
